/**
 * BULGU-016: Oturum JWT'si httpOnly cerezde (pm_auth_token); localStorage'da
 * yalnızca rol/profil metadata tutulur. Eski token anahtarları temizlenir.
 */

export const AUTH_SESSION_KIND_KEY = 'authSessionKind';
export const LEGACY_JWT_STORAGE_KEYS = ['token', 'tokenTenant', 'superAdminToken'];

export function purgeLegacyJwtFromStorage() {
  for (const key of LEGACY_JWT_STORAGE_KEYS) {
    localStorage.removeItem(key);
  }
  sessionStorage.removeItem('token');
}

export function setAuthSessionKind(kind) {
  if (kind) localStorage.setItem(AUTH_SESSION_KIND_KEY, kind);
  else localStorage.removeItem(AUTH_SESSION_KIND_KEY);
}

export function getAuthSessionKind() {
  return localStorage.getItem(AUTH_SESSION_KIND_KEY);
}

export function getStoredDoctorUser() {
  try {
    const raw = localStorage.getItem('user');
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

export function getDoctorSessionCacheKey() {
  return getStoredDoctorUser()?.id || "";
}

export function getStoredTenantId() {
  return localStorage.getItem('tenantId');
}

export function getStoredSuperAdminUser() {
  try {
    const raw = localStorage.getItem('superAdminUser');
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

export function isDoctorSessionActive() {
  const kind = getAuthSessionKind();
  if (kind === 'tenant' || kind === 'super_admin') return false;
  const user = getStoredDoctorUser();
  return Boolean(user?.role && ['doctor', 'admin'].includes(user.role));
}

export function isTenantSessionActive() {
  if (getAuthSessionKind() === 'super_admin') return false;
  if (getAuthSessionKind() === 'doctor') return false;
  if (getAuthSessionKind() === 'tenant') {
    return Boolean(localStorage.getItem('tenantInfo') || localStorage.getItem('tenantId'));
  }
  // Geçiş: kind henüz yazılmamış eski oturum — tenant metadata var, doctor user yok
  const hasTenantMeta = Boolean(localStorage.getItem('tenantInfo') || localStorage.getItem('tenantId'));
  return hasTenantMeta && !isDoctorSessionActive();
}

export function isSuperAdminSessionActive() {
  return getAuthSessionKind() === 'super_admin' && Boolean(getStoredSuperAdminUser());
}

/** Route guard'lar için JWT yerine profil + session kind. */
export function hasSessionForRoles(allowedRoles) {
  if (!Array.isArray(allowedRoles) || !allowedRoles.length) return false;

  if (allowedRoles.includes('tenant') && isTenantSessionActive()) return true;

  if (allowedRoles.includes('super_admin') && isSuperAdminSessionActive()) return true;

  if ((allowedRoles.includes('doctor') || allowedRoles.includes('admin')) && isDoctorSessionActive()) {
    const role = getStoredDoctorUser()?.role;
    return allowedRoles.includes(role);
  }

  return false;
}

export function clearDoctorSessionStorage() {
  localStorage.removeItem('user');
  localStorage.removeItem('doctorId');
  if (getAuthSessionKind() === 'doctor') setAuthSessionKind(null);
  purgeLegacyJwtFromStorage();
}

export function clearTenantSessionStorage() {
  localStorage.removeItem('tenantId');
  localStorage.removeItem('tenantInfo');
  localStorage.removeItem('tenantRequiresCheckout');
  if (getAuthSessionKind() === 'tenant') setAuthSessionKind(null);
  purgeLegacyJwtFromStorage();
}

export function clearSuperAdminSessionStorage() {
  localStorage.removeItem('superAdminUser');
  if (getAuthSessionKind() === 'super_admin') setAuthSessionKind(null);
  purgeLegacyJwtFromStorage();
}

export function clearAllAuthSessions() {
  clearDoctorSessionStorage();
  clearTenantSessionStorage();
  clearSuperAdminSessionStorage();
  setAuthSessionKind(null);
  purgeLegacyJwtFromStorage();
}

/** Login sonrası: profil kaydet, JWT'yi localStorage'a yazma. */
export function establishDoctorSession(userProfile) {
  purgeLegacyJwtFromStorage();
  clearTenantSessionStorage();
  setAuthSessionKind('doctor');
  if (userProfile) {
    localStorage.setItem('user', JSON.stringify(userProfile));
    if (userProfile.id) localStorage.setItem('doctorId', userProfile.id);
  }
}

export function establishTenantSession({ tenant, requiresCheckout }) {
  purgeLegacyJwtFromStorage();
  clearDoctorSessionStorage();
  setAuthSessionKind('tenant');
  if (tenant?.id) localStorage.setItem('tenantId', tenant.id);
  if (tenant) localStorage.setItem('tenantInfo', JSON.stringify(tenant));
  localStorage.setItem('tenantRequiresCheckout', requiresCheckout ? 'true' : 'false');
}

export function establishSuperAdminSession(user) {
  purgeLegacyJwtFromStorage();
  clearDoctorSessionStorage();
  clearTenantSessionStorage();
  setAuthSessionKind('super_admin');
  if (user) localStorage.setItem('superAdminUser', JSON.stringify(user));
}
